/**
 * OfflineSyncBadge — Visual indicator showing pending offline actions.
 * Displays a floating badge with count. Clicking opens the SyncQueuePanel.
 */
import { useState, useEffect, useCallback } from 'react';
import { CloudOff, RefreshCw, Check } from 'lucide-react';
import { getQueueCount } from '@/lib/offlineDb';
import { syncQueue } from '@/lib/offlineQueue';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SyncQueuePanel } from './SyncQueuePanel';

export function OfflineSyncBadge() {
  const [count, setCount] = useState(0);
  const [isSyncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [panelOpen, setPanelOpen] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const c = await getQueueCount();
      setCount(c);
    } catch {
      // IndexedDB not available
    }
  }, []);

  useEffect(() => {
    refreshCount();

    const handleQueueChange = () => refreshCount();
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('offline-queue-changed', handleQueueChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Poll every 10s as fallback
    const interval = setInterval(refreshCount, 10_000);

    return () => {
      window.removeEventListener('offline-queue-changed', handleQueueChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [refreshCount]);

  const handleClick = async () => {
    if (count > 0) {
      // Open panel to show details
      setPanelOpen(true);
    } else if (!navigator.onLine) {
      // Nothing to do when offline with no actions
      return;
    }
  };

  // Don't render if no pending actions and online
  if (count === 0 && !isOffline) return null;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClick}
              className={`fixed bottom-4 right-4 z-[150] flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium shadow-lg transition-all duration-300 text-primary-foreground ${
                justSynced
                  ? 'bg-green-600 dark:bg-green-500'
                  : isOffline
                    ? 'bg-muted-foreground'
                    : 'bg-primary'
              }`}
            >
              {justSynced ? (
                <Check className="h-4 w-4" />
              ) : isSyncing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <CloudOff className="h-4 w-4" />
              )}

              {justSynced ? (
                'Sincronizado!'
              ) : count > 0 ? (
                <>
                  <span className="tabular-nums">{count}</span>
                  <span className="hidden sm:inline">
                    {count === 1 ? 'ação pendente' : 'ações pendentes'}
                  </span>
                </>
              ) : (
                <span>Offline</span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {isOffline
              ? 'Sem conexão — ações serão sincronizadas ao reconectar'
              : count > 0
                ? 'Clique para ver detalhes'
                : 'Tudo sincronizado'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SyncQueuePanel open={panelOpen} onOpenChange={setPanelOpen} />
    </>
  );
}
